import type { TaskId } from '@/core/content/ids';
import type { ContentRegistry } from '@/core/content/registry';
import type { EventMatcher, SuggestionDef } from '@/core/content/schemas/progressionDefs';
import type { DomainEvent } from '@/core/events/types';
import type { GameSave } from '@/core/persistence/schema/v1';
import { evaluateAll } from './conditions';
import type { HandlerEnv } from './placement/handlers';
import { placedGeoms, roomGeometry } from './placement/rules';
import { computeCoziness } from './coziness';
import { grantRewards } from './rewards';
import { createRng } from './rng';

type Runtime = GameSave['suggestions']['tasks'][string] & {};
type RepeatableEntry = GameSave['suggestions']['repeatable']['active'][number];

const MAX_ROUNDS = 12;

// ---- matching ------------------------------------------------------------------------------------

function eventMatches(content: ContentRegistry, e: DomainEvent, where: EventMatcher | undefined): boolean {
  if (!where) return true;
  const rec = e as Record<string, unknown>;
  const itemId = typeof rec.itemId === 'string' ? rec.itemId : undefined;
  const def = itemId ? content.items.get(itemId as never) : undefined;
  if (where.itemId && itemId !== where.itemId) return false;
  if (where.itemIdsAny && (!itemId || !where.itemIdsAny.includes(itemId as never))) return false;
  if (where.kindsAny && (!def || !where.kindsAny.includes(def.kind))) return false;
  if (where.tagsAny && (!def || !where.tagsAny.some((t) => def.tags.includes(t)))) return false;
  if (where.roomId && rec.roomId !== where.roomId) return false;
  if (where.zone && !(Array.isArray(rec.zones) && rec.zones.includes(where.zone))) return false;
  if (where.rightSpot !== undefined && rec.rightSpot !== where.rightSpot) return false;
  if (where.fromBag !== undefined && rec.fromBag !== where.fromBag) return false;
  if (where.acquiredBy && rec.acquiredBy !== where.acquiredBy) return false;
  if (where.rotated !== undefined) {
    const rotated = e.type === 'ITEM_ROTATED' || (typeof rec.rotation === 'number' && rec.rotation !== 0);
    if (rotated !== where.rotated) return false;
  }
  if (where.boxDefIdsAny && !where.boxDefIdsAny.includes(rec.boxDefId as never)) return false;
  if (where.miniGameId && rec.miniGameId !== where.miniGameId) return false;
  if (where.level !== undefined && rec.level !== where.level) return false;
  if (where.passed !== undefined && rec.passed !== where.passed) return false;
  if (where.completed !== undefined && rec.completed !== where.completed) return false;
  if (where.chapterId && rec.chapterId !== where.chapterId) return false;
  if (where.npcId && rec.npcId !== where.npcId) return false;
  return true;
}

/** How many items currently placed satisfy `where` (order-proof: placing first then activating still counts). */
function placedCount(content: ContentRegistry, s: GameSave, where: EventMatcher): number {
  let n = 0;
  for (const roomId of ['home', 'minimart'] as const) {
    if (where.roomId && where.roomId !== roomId) continue;
    const geo = roomGeometry(content, s, roomId);
    const right =
      where.rightSpot === undefined ? null : new Set(computeCoziness(content, s, roomId).rightSpotIds);
    for (const { instanceId, itemId, geom } of placedGeoms(content, s, roomId)) {
      const def = content.items.get(itemId);
      if (!def) continue;
      if (where.itemId && itemId !== where.itemId) continue;
      if (where.itemIdsAny && !where.itemIdsAny.includes(itemId)) continue;
      if (where.kindsAny && !where.kindsAny.includes(def.kind)) continue;
      if (where.tagsAny && !where.tagsAny.some((t) => def.tags.includes(t))) continue;
      if (where.zone && !geo.zonesOf(geom.cells).includes(where.zone)) continue;
      if (where.acquiredBy && s.inventory.instances[instanceId]?.acquiredBy !== where.acquiredBy) continue;
      if (right && right.has(instanceId) !== where.rightSpot) continue;
      n += 1;
    }
  }
  return n;
}

/** Progress as { current, target } for UI and completion. */
export function taskProgress(
  content: ContentRegistry,
  s: GameSave,
  def: SuggestionDef,
  rt: Runtime | undefined,
): { current: number; target: number } {
  const c = def.criteria;
  switch (c.kind) {
    case 'event':
      return {
        current: c.distinctBy ? (rt?.distinctSeen?.length ?? 0) : (rt?.eventCount ?? 0),
        target: c.count,
      };
    case 'stat': {
      const value = (s.stats.counters[c.stat] ?? 0) - (c.relative ? (rt?.baseline ?? 0) : 0);
      return { current: Math.max(0, value), target: c.target };
    }
    case 'conditions':
      return { current: evaluateAll(content, s, c.all).ok ? 1 : 0, target: 1 };
    case 'placed':
      return { current: placedCount(content, s, c.where), target: c.count };
  }
}

/** ICU arguments for a suggestion's hint (`{price}` from `hintPriceItemId`); `undefined` when it needs none. */
export function suggestionHintParams(
  content: ContentRegistry,
  def: SuggestionDef,
): { price: number } | undefined {
  if (!def.hintPriceItemId) return undefined;
  return { price: content.items.get(def.hintPriceItemId)?.buyPrice ?? 0 };
}

// ---- engine --------------------------------------------------------------------------------------

function activate(draft: GameSave, def: SuggestionDef, rt: Runtime): DomainEvent {
  rt.status = 'active';
  rt.activatedAtPlayMs = draft.clock.playTimeMs;
  rt.eventCount = 0;
  rt.distinctSeen = [];
  if (def.criteria.kind === 'stat' && def.criteria.relative)
    rt.baseline = draft.stats.counters[def.criteria.stat] ?? 0;
  return { type: 'TASK_ACTIVATED', taskId: def.id };
}

function rewardKey(def: SuggestionDef, instanceKey?: string): string {
  return `task:${instanceKey ?? def.id}`;
}

function grantTask(
  draft: GameSave,
  content: ContentRegistry,
  def: SuggestionDef,
  instanceKey?: string,
): DomainEvent[] {
  return [
    {
      type: 'TASK_CLAIMED',
      taskId: def.id,
      ...(instanceKey ? { instanceKey } : {}),
      rewards: [...def.rewards],
    },
    ...grantRewards(draft, content, rewardKey(def, instanceKey), def.rewards),
  ];
}

function countEvents(
  content: ContentRegistry,
  def: SuggestionDef,
  rt: Runtime,
  events: readonly DomainEvent[],
): void {
  const c = def.criteria;
  if (c.kind !== 'event') return;
  for (const e of events) {
    if (!c.events.includes(e.type)) continue;
    if ('origin' in e && (e.origin === 'undo' || e.origin === 'redo')) continue;
    if (!eventMatches(content, e, c.where)) continue;
    if (c.distinctBy) {
      const raw = (e as Record<string, unknown>)[c.distinctBy];
      const key = typeof raw === 'string' ? raw : '';
      if (key && !(rt.distinctSeen ?? []).includes(key)) rt.distinctSeen = [...(rt.distinctSeen ?? []), key];
    } else {
      rt.eventCount = (rt.eventCount ?? 0) + 1;
    }
  }
}

/**
 * Pipeline step e (content-schemas §14.9). One tutorial task at a time; optional tasks after T10; repeatable
 * tasks fill the remaining slots. No deadlines, nothing is ever lost (plan §3.7).
 */
export function suggestionSystem(
  draft: GameSave,
  events: readonly DomainEvent[],
  env: HandlerEnv,
): DomainEvent[] {
  const { content } = env;
  const S = draft.suggestions;
  const out: DomainEvent[] = [];
  const byId = new Map(content.suggestions.map((d) => [d.id, d]));
  const rtOf = (id: TaskId): Runtime => (S.tasks[id] ??= { status: 'locked' });

  // Progress events only for tasks that were already active before this batch.
  for (const def of content.suggestions) {
    if (def.kind === 'repeatable') continue;
    const rt = S.tasks[def.id];
    if (rt?.status === 'active') countEvents(content, def, rt, events);
  }
  for (const entry of S.repeatable.active) {
    const def = byId.get(entry.taskId);
    if (def && entry.runtime.status === 'active') countEvents(content, def, entry.runtime, events);
  }

  for (let round = 0; round < MAX_ROUNDS; round++) {
    let changed = false;

    // 1. Tutorial.
    const tutorial = content.suggestions.filter((d) => d.kind === 'tutorial');
    const current = tutorial.find((d) => S.tasks[d.id]?.status !== 'claimed');
    if (current) {
      const rt = rtOf(current.id);
      if (
        (rt.status === 'locked' || rt.status === 'available') &&
        evaluateAll(content, draft, current.requires).ok
      ) {
        out.push(activate(draft, current, rt));
        changed = true;
      }
    }
    const tutorialDone = tutorial.length === 0 || tutorial.every((d) => S.tasks[d.id]?.status === 'claimed');

    // 2. Optional (after the tutorial).
    const slots = content.balance.optionalSuggestionsVisible;
    let optionalBlocked = true;
    if (tutorialDone) {
      const candidates = content.suggestions
        .filter((d) => d.kind === 'optional')
        .filter((d) => {
          const st = S.tasks[d.id]?.status ?? 'locked';
          return (st === 'locked' || st === 'available') && evaluateAll(content, draft, d.requires).ok;
        })
        .sort(
          (a, b) =>
            (S.tasks[a.id]?.activatedAtPlayMs ?? -1) - (S.tasks[b.id]?.activatedAtPlayMs ?? -1) ||
            a.order - b.order,
        );
      optionalBlocked = candidates.length === 0;
      while (S.optionalActive.length + S.repeatable.active.length < slots && candidates.length > 0) {
        const def = candidates.shift();
        if (!def) break;
        out.push(activate(draft, def, rtOf(def.id)));
        S.optionalActive.push(def.id);
        changed = true;
      }
    }

    // 3. Repeatable: fill empty slots when no optional can take them, after the claim cooldown.
    const cooldownOk =
      S.repeatable.lastClaimAtPlayMs === null ||
      draft.clock.playTimeMs - S.repeatable.lastClaimAtPlayMs >=
        content.balance.repeatableClaimCooldownSec * 1000;
    if (tutorialDone && optionalBlocked && cooldownOk) {
      const pool = content.suggestions.filter(
        (d) =>
          d.kind === 'repeatable' &&
          !S.repeatable.active.some((a) => a.taskId === d.id) &&
          evaluateAll(content, draft, d.requires).ok,
      );
      const rng = createRng(S.repeatable.rngState);
      while (S.optionalActive.length + S.repeatable.active.length < slots && pool.length > 0) {
        const def = pool.splice(rng.int(0, pool.length - 1), 1)[0];
        if (!def) break;
        S.repeatable.seq += 1;
        const entry: RepeatableEntry = {
          instanceKey: `rep#${String(S.repeatable.seq)}`,
          taskId: def.id as RepeatableEntry['taskId'],
          runtime: { status: 'locked' },
        };
        out.push({ ...activate(draft, def, entry.runtime), instanceKey: entry.instanceKey } as DomainEvent);
        S.repeatable.active.push(entry);
        changed = true;
      }
      S.repeatable.rngState = rng.state;
    }

    // 4. Completion (+ auto-claim for the tutorial).
    const actives: { def: SuggestionDef; rt: Runtime; instanceKey?: string }[] = [];
    for (const def of content.suggestions) {
      const rt = S.tasks[def.id];
      if (def.kind !== 'repeatable' && rt?.status === 'active') actives.push({ def, rt });
    }
    for (const entry of S.repeatable.active) {
      const def = byId.get(entry.taskId);
      if (def && entry.runtime.status === 'active')
        actives.push({ def, rt: entry.runtime, instanceKey: entry.instanceKey });
    }
    for (const { def, rt, instanceKey } of actives) {
      const p = taskProgress(content, draft, def, rt);
      if (p.current < p.target) continue;
      rt.status = 'completed';
      out.push({
        type: 'TASK_COMPLETED',
        taskId: def.id,
        kind: def.kind,
        ...(instanceKey ? { instanceKey } : {}),
      });
      changed = true;
      if (def.autoClaim) {
        rt.status = 'claimed';
        out.push(...grantTask(draft, content, def, instanceKey));
        if (def.kind === 'optional') S.optionalActive = S.optionalActive.filter((id) => id !== def.id);
      }
    }
    if (!changed) break;
  }
  return out;
}

/** claimReward for a task (content-schemas §14.9 claim). */
export function claimTask(
  draft: GameSave,
  content: ContentRegistry,
  taskId: TaskId,
  instanceKey?: string,
):
  | { ok: true; events: DomainEvent[] }
  | { ok: false; error: 'NOT_FOUND' | 'NOT_COMPLETED' | 'ALREADY_CLAIMED' } {
  const def = content.suggestions.find((d) => d.id === taskId);
  if (!def) return { ok: false, error: 'NOT_FOUND' };
  const S = draft.suggestions;
  if (def.kind === 'repeatable') {
    const entry = S.repeatable.active.find((a) => a.instanceKey === instanceKey);
    if (!entry) return { ok: false, error: 'NOT_FOUND' };
    if (entry.runtime.status !== 'completed') return { ok: false, error: 'NOT_COMPLETED' };
    S.repeatable.active = S.repeatable.active.filter((a) => a !== entry);
    S.repeatable.lastClaimAtPlayMs = draft.clock.playTimeMs;
    return { ok: true, events: grantTask(draft, content, def, entry.instanceKey) };
  }
  const rt = S.tasks[taskId];
  if (!rt) return { ok: false, error: 'NOT_FOUND' };
  if (rt.status === 'claimed') return { ok: false, error: 'ALREADY_CLAIMED' };
  if (rt.status !== 'completed') return { ok: false, error: 'NOT_COMPLETED' };
  rt.status = 'claimed';
  S.optionalActive = S.optionalActive.filter((id) => id !== taskId);
  return { ok: true, events: grantTask(draft, content, def) };
}

/**
 * Would "Đổi gợi ý" bring something different? An optional card needs another optional that could take its
 * slot; a repeatable card another template. Otherwise S08 hides the button (ux-screens §3.8).
 */
export function hasSwapAlternative(content: ContentRegistry, s: GameSave, taskId: TaskId): boolean {
  const def = content.suggestions.find((d) => d.id === taskId);
  if (!def || def.kind === 'tutorial') return false;
  const S = s.suggestions;
  return content.suggestions.some((d) => {
    if (d.kind !== def.kind || d.id === taskId) return false;
    if (!evaluateAll(content, s, d.requires).ok) return false;
    if (d.kind === 'repeatable') return !S.repeatable.active.some((a) => a.taskId === d.id);
    const st = S.tasks[d.id]?.status ?? 'locked';
    return st === 'locked' || st === 'available';
  });
}

/** Swap a suggestion for another, free and without penalty (plan §3.7). */
export function swapTask(
  draft: GameSave,
  content: ContentRegistry,
  taskId: TaskId,
  instanceKey?: string,
): { ok: true; events: DomainEvent[] } | { ok: false; error: 'NOT_FOUND' | 'INVALID_ARGUMENT' } {
  const def = content.suggestions.find((d) => d.id === taskId);
  if (!def || def.kind === 'tutorial') return { ok: false, error: 'INVALID_ARGUMENT' };
  const S = draft.suggestions;
  if (def.kind === 'repeatable') {
    if (!S.repeatable.active.some((a) => a.instanceKey === instanceKey))
      return { ok: false, error: 'NOT_FOUND' };
    S.repeatable.active = S.repeatable.active.filter((a) => a.instanceKey !== instanceKey);
    // The next system pass refills the slot with a different template (cooldown does not apply to swaps).
    S.repeatable.lastClaimAtPlayMs = null;
    return { ok: true, events: [{ type: 'TASK_SWAPPED', taskId, replacedBy: null }] };
  }
  const rt = S.tasks[taskId];
  if (rt?.status !== 'active') return { ok: false, error: 'NOT_FOUND' };
  rt.status = 'available';
  // "Back of the queue": candidates are ordered by this timestamp first.
  rt.activatedAtPlayMs = draft.clock.playTimeMs;
  rt.eventCount = 0;
  rt.distinctSeen = [];
  S.optionalActive = S.optionalActive.filter((id) => id !== taskId);
  return { ok: true, events: [{ type: 'TASK_SWAPPED', taskId, replacedBy: null }] };
}
